import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { BuildingService } from '../../../../resources/building/building.service';
import { Cluster } from '../../../../resources/cluster/cluster.interface';
import { ClusterService } from '../../../../resources/cluster/cluster.service';
import { takeUntil, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Building } from '../../../../resources/building/building.interface';
import { ClusterTotalMetrics } from './cluster-totals.interface';
import { ScrollToService } from '../../../../utilities/scroll-to/scroll-to.service';

@Component({
  selector: 'sg2-cluster-totals',
  templateUrl: './cluster-totals.component.html',
  styleUrls: ['./cluster-totals.component.less']
})
export class ClusterTotalsComponent implements  OnDestroy, OnInit {

  /** Parent building. */
  @Input() building: Building;

  /** Current visible clusters. */
  public clusters: Cluster[];

  /** Destroy hook. */
  private destroy$ = new Subject<any>();

  /** Table columns. */
  public tableColumns = [
    'code',
    'name',
    'surface',
    'fno',
    'ratio',
    'bvo',
    'nfno',
    'san',
    'ver',
    'bui'
  ];

  /** Table totals. */
  public totals: ClusterTotalMetrics;

  constructor(
    public buildingService: BuildingService,
    private clusterService: ClusterService,
    private scrollToService: ScrollToService
  ) { }

  ngOnInit() {
    this._trackClusters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Scroll to cluster.
   *
   * @param id The id of the cluster to scroll to.
   */
  public goToCluster(id: Cluster['_id']): void {
    this.scrollToService.find(id, { offset: -20 });
  }

  /**
   * Set custom values to clusters.
   *
   * By setting these values directly on the cluster, there's no need for using
   * getters in the template, which trigger for every change detection.
   * Since the data can *only* change on store updates, the custom values should
   * only be updated after a store broadcast.
   *
   * @param clusters The clusters to set the values for.
   * @returns The clusters with added custom values.
   */
  private _setCustomValues(clusters: Cluster[]): Cluster[] {
    for (const cluster of clusters) {
      cluster.customValues = {
        bui: this.clusterService.getBui(cluster),
        bvo: this.clusterService.getBvo(cluster),
        fno: this.clusterService.getFno(cluster),
        nfno: this.clusterService.getNfno(cluster),
        san: this.clusterService.getSan(cluster),
        surface: this.clusterService.getSurface(cluster),
        ver: this.clusterService.getVer(cluster),
      };
    }
    return clusters;
  }

  /**
   * Set the totals data.
   *
   * Save the data into a a special totals object, to prevent using
   * getters in the template. The object is updated on each store update.
   *
   * @todo Move these functions to `buildingService`?
   *
   * @param clusters The clusters (with `customValues` property) to get totals for.
   */
  private _setTotals(clusters: Cluster[]): ClusterTotalMetrics {
    const totals = {
      bui: clusters.reduce((acc, cluster) => acc + cluster.customValues['bui'] || 0, 0),
      bvo: clusters.reduce((acc, cluster) => acc + cluster.customValues['bvo'] || 0, 0),
      fno: clusters.reduce((acc, cluster) => acc + cluster.customValues['fno'] || 0, 0),
      nfno: clusters.reduce((acc, cluster) => acc + cluster.customValues['nfno'] || 0, 0),
      ratio: 0,
      san: clusters.reduce((acc, cluster) => acc + cluster.customValues['san'] || 0, 0),
      surface: clusters.reduce((acc, cluster) => acc + cluster.customValues['surface'] || 0, 0),
      ver: clusters.reduce((acc, cluster) => acc + cluster.customValues['ver'] || 0, 0),
    };
    return totals;
  }

  /**
   * Keep track of clusters.
   *
   * Pipe:
   * - Filter clusters by current building
   * - Filter inactive clusters
   * - Remove all inactive areas
   */
  private _trackClusters(): void {
    this.clusterService.cluster$.pipe(
      takeUntil(this.destroy$),
      map(clusters => clusters.filter(cl => cl.buildingId === this.building._id)),
      map(clusters => clusters.filter(cl => cl.active)),
      map(clusters => clusters.map(cl => cl = { ...cl, areas: cl.areas.filter(a => a.active) })),
    ).subscribe(clusters => {
      this.clusters = this._setCustomValues(clusters);
      this.totals = this._setTotals(this.clusters);
    });
  }
}
